/* ===== kpers — design tokens =====
 * Fonte única de verdade para os temas MUI v4/v5, CSS globais e splash.
 * Gramática visual do painel RFRouter (borda-solunet/painel/web/src/index.css):
 * fundo #f3f6f8, superfícies #fff/#f7fafb, linha #e6eaf3, tinta #121a32,
 * Inter, raio 8 em tudo (selos 6), sombra de cartão quase nula.
 *
 * REGRA: a cor da marca NUNCA é fixa aqui — ela chega por parâmetro
 * (colorMode.primaryColorLight/Dark → theme.palette.primary.main).
 * O único hex "de marca" neste arquivo é o fallback #0B2239 (azul-noite Digite AI) para input inválido.
 */

export const INK = {
  900: "#121a32",
  800: "#202d49",
  700: "#364964",
  600: "#4c597b",
  500: "#64718a",
  400: "#8a98af",
};

export const LIGHT = {
  page: "#f3f6f8",
  muted: "#f7fafb",
  paper: "#ffffff",
  elevated: "#ffffff",
  line: "#e6eaf3",
  lineRow: "#edf1f7",
  lineStrong: "#cfd8e6",
  inputBorder: "#dbe3ef",
  text: INK[900],
  textSecondary: INK[500],
  textMuted: INK[400],
  label: INK[700],
};

// O painel de origem não tem modo escuro: os neutros abaixo são os que o kpers já usava.
export const DARK = {
  page: "#0b1220",
  muted: "#111a2b",
  paper: "#172033",
  elevated: "#1B2639",
  line: "#243047",
  lineRow: "#1f2a40",
  lineStrong: "#334155",
  inputBorder: "#243047",
  text: "#e5e9f2",
  textSecondary: "#94a3b8",
  textMuted: "#64748b",
  label: "#cbd5e1",
};

// Os nomes da escala continuam (≈100 arquivos consomem xs..xl); o valor é um só.
export const RADIUS = { xs: 8, sm: 8, md: 8, lg: 8, xl: 8, badge: 6, pill: 9999 };

export const SPACE = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
};

// Barra superior: 70px como no painel; 56px abaixo de 960px (como já era).
export const TOPBAR = { height: 70, compact: 56 };

export const MOTION = {
  fast: "120ms ease",
  normal: "180ms ease",
  slow: "240ms cubic-bezier(0.2, 0, 0, 1)",
};

export const SHADOW = {
  card: "0 2px 5px #10283f03",
  soft: "0 6px 20px rgba(20,39,69,0.07)",
  lift: "0 16px 55px #14274520",
  glow: (brand) => `0 0 0 3px ${brandAlpha(brand, 0.16)}`,
};

// Mesma geometria; no escuro a sombra precisa de mais opacidade para existir.
const SHADOW_DARK = {
  card: "0 2px 5px rgba(0,0,0,0.18)",
  soft: "0 6px 20px rgba(0,0,0,0.32)",
  lift: "0 16px 55px rgba(0,0,0,0.5)",
};

export const FONT =
  "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export const TYPE = {
  pageTitle: { fontSize: 22, fontWeight: 700, lineHeight: 1.25 },
  // Título de página do painel (Title/PageHeader). pageTitle segue numérico: alimenta o h5 do tema.
  pageHeading: {
    fontSize: "clamp(22px, 2.1vw, 28px)",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.035em",
  },
  sectionTitle: { fontSize: 14, fontWeight: 600 },
  body: { fontSize: 14 },
  label: { fontSize: 12, fontWeight: 500 },
  groupTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  kpi: { fontSize: 24, fontWeight: 600, fontVariantNumeric: "tabular-nums" },
};

export const SEMANTIC = {
  success: "#16a34a",
  warning: "#d97706",
  error: "#dc2626",
  info: "#2563eb",
};

// Selos de status do painel (texto sobre fundo suave), nos 4 tons.
export const STATUS = {
  ok: { fg: "#0f7a5c", bg: "#e6f7f2" },
  warn: { fg: "#8a5a00", bg: "#fff4d6" },
  down: { fg: "#b3261e", bg: "#fde8e8" },
  neutral: { fg: "#4c597b", bg: "#eef2f7" },
};

const STATUS_DARK = {
  ok: { fg: "#5fd3b0", bg: "rgba(15,122,92,0.24)" },
  warn: { fg: "#f0c060", bg: "rgba(217,149,6,0.18)" },
  down: { fg: "#f58a84", bg: "rgba(179,38,30,0.28)" },
  neutral: { fg: "#a9b6cf", bg: "rgba(148,163,184,0.16)" },
};

const FALLBACK_BRAND = "#0B2239";

/**
 * Aceita #rgb, #rrggbb ou #rrggbbaa (descarta o alpha) e devolve #rrggbb
 * minúsculo. Qualquer outra coisa → fallback.
 */
export function normalizeHex(hex, fallback = FALLBACK_BRAND) {
  if (typeof hex !== "string") return fallback;
  const h = hex.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(h)) {
    return (
      "#" +
      h
        .split("")
        .map((c) => c + c)
        .join("")
        .toLowerCase()
    );
  }
  if (/^[0-9a-f]{6}$/i.test(h)) return "#" + h.toLowerCase();
  if (/^[0-9a-f]{8}$/i.test(h)) return "#" + h.slice(0, 6).toLowerCase();
  return fallback;
}

function toRgb(hex) {
  const h = normalizeHex(hex).slice(1);
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function toHex(r, g, b) {
  const c = (v) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** 'rgba(r,g,b,alpha)' a partir da cor da marca. */
export function brandAlpha(hex, alpha) {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * pct em -1..1: negativo escurece (mistura com preto), positivo clareia
 * (mistura com branco). Devolve #rrggbb.
 */
export function shadeBrand(hex, pct) {
  const [r, g, b] = toRgb(hex);
  const p = Math.max(-1, Math.min(1, Number(pct) || 0));
  const target = p < 0 ? 0 : 255;
  const t = Math.abs(p);
  return toHex(
    r + (target - r) * t,
    g + (target - g) * t,
    b + (target - b) * t
  );
}

function relativeLuminance(hex) {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Marca legível no modo escuro: se a cor configurada for muito escura
 * (luminância < 0.12 — ex.: verde/azul-marinho fechados), clareia até ficar
 * visível sobre as superfícies DARK. No modo claro devolve a cor intacta.
 */
export function legibleBrand(hex, isDark) {
  if (!isDark) return hex;
  let out = hex;
  for (let i = 0; i < 6 && relativeLuminance(out) < 0.12; i++) {
    out = shadeBrand(out, 0.18);
  }
  return out;
}

/** Texto sobre a marca sólida: branco ou ink-900, pelo contraste WCAG. */
export function brandOn(hex) {
  const L = relativeLuminance(hex);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastInk = (L + 0.05) / (relativeLuminance(INK[900]) + 0.05);
  return contrastWhite >= contrastInk ? "#ffffff" : INK[900];
}

function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// Acima disso o texto do menu (branco a 68%) cai abaixo de 4.5:1 sobre a tinta.
const NAV_MAX_LUMINANCE = 0.035;

/**
 * Cores do menu lateral, derivadas das duas cores da empresa (o equivalente
 * a --brand-ink/--brand-accent do painel): a mais escura vira a tinta sólida
 * do fundo (escurecida se preciso) e a mais viva vira o destaque do item
 * ativo (clareada até ter contraste sobre a tinta). Com uma cor só, as duas
 * saem dela; sem cor nenhuma, do azul-noite de fallback.
 */
export function getNavTokens(lightHex, darkHex) {
  const a = normalizeHex(lightHex);
  const b = normalizeHex(darkHex, a);
  const aIsDarker = relativeLuminance(a) <= relativeLuminance(b);
  let bg = aIsDarker ? a : b;
  for (let i = 0; i < 16 && relativeLuminance(bg) > NAV_MAX_LUMINANCE; i++) {
    bg = shadeBrand(bg, -0.15);
  }
  let accent = aIsDarker ? b : a;
  for (let i = 0; i < 16 && contrastRatio(accent, bg) < 4.5; i++) {
    accent = shadeBrand(accent, 0.15);
  }
  return {
    bg,
    accent,
    accentSoft: brandAlpha(accent, 0.12),
    accentSoftHover: brandAlpha(accent, 0.18),
    accentText: shadeBrand(accent, 0.88),
    text: "rgba(255,255,255,0.68)",
    textStrong: "#ffffff",
    textMuted: "rgba(255,255,255,0.56)",
    hover: "rgba(255,255,255,0.075)",
    line: "rgba(255,255,255,0.125)",
  };
}

const TINT_ALPHA = { soft: 0.1, medium: 0.16, strong: 0.24 };

/** Tinta da marca: soft = .10, medium = .16, strong = .24. */
export function tint(hex, level = "soft") {
  return brandAlpha(hex, TINT_ALPHA[level] || TINT_ALPHA.soft);
}

/**
 * Objeto único consumido pelos dois temas (v4 e v5) e pelas CSS vars.
 */
export function getTokens(mode, primaryHex) {
  const isDark = mode === "dark";
  const brand = legibleBrand(normalizeHex(primaryHex), isDark);
  const brandHover = shadeBrand(brand, -0.12);
  const onBrand = brandOn(brand);
  const brandTint = {
    soft: tint(brand, "soft"),
    medium: tint(brand, "medium"),
    strong: tint(brand, "strong"),
  };
  const surface = isDark ? DARK : LIGHT;
  const shadow = isDark ? SHADOW_DARK : SHADOW;
  return {
    mode: isDark ? "dark" : "light",
    brand,
    brandHover,
    brandOn: onBrand,
    tint: brandTint,
    // Aliases semânticos usados pelos workspaces ricos (ex.: Atendimento).
    // Mantêm uma única origem whitelabel e evitam cores locais divergentes.
    accent: brand,
    accentHover: brandHover,
    accentOn: onBrand,
    accentTint: brandTint,
    ...surface,
    rowHover: brandAlpha(brand, isDark ? 0.08 : 0.045),
    radius: RADIUS,
    space: SPACE,
    motion: MOTION,
    shadow: {
      card: shadow.card,
      soft: shadow.soft,
      lift: shadow.lift,
      glow: SHADOW.glow(brand),
    },
    font: FONT,
    type: TYPE,
    semantic: SEMANTIC,
    status: isDark ? STATUS_DARK : STATUS,
    // App.js troca por getNavTokens(clara, escura); aqui só há a cor do modo atual.
    // Getter: getTokens roda a cada render de selo/cartão e quase ninguém lê `nav`.
    get nav() {
      return getNavTokens(primaryHex, primaryHex);
    },
  };
}

/**
 * Publica os tokens como CSS vars em :root (premium.css, Kanban, toasts, splash)
 * e mantém a var legada --primaryColor.
 */
export function applyCssVars(tokens) {
  if (typeof document === "undefined" || !tokens) return;
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, String(v));
  set("--kp-page", tokens.page);
  set("--kp-muted", tokens.muted);
  set("--kp-paper", tokens.paper);
  set("--kp-line", tokens.line);
  set("--kp-line-row", tokens.lineRow);
  set("--kp-input-border", tokens.inputBorder);
  set("--kp-label", tokens.label);
  set("--kp-text", tokens.text);
  set("--kp-text-2", tokens.textSecondary);
  set("--kp-text-3", tokens.textMuted);
  set("--kp-brand", tokens.brand);
  set("--kp-brand-on", tokens.brandOn);
  set("--kp-brand-tint", tokens.tint.soft);
  set("--kp-brand-tint-strong", tokens.tint.strong);
  set("--kp-radius", `${tokens.radius.sm}px`);
  set("--kp-radius-xs", `${tokens.radius.xs}px`);
  set("--kp-radius-md", `${tokens.radius.md}px`);
  set("--kp-radius-lg", `${tokens.radius.lg}px`);
  set("--kp-radius-xl", `${tokens.radius.xl}px`);
  set("--kp-radius-badge", `${tokens.radius.badge}px`);
  set("--kp-nav-bg", tokens.nav.bg);
  set("--kp-nav-accent", tokens.nav.accent);
  set("--kp-shadow-card", tokens.shadow.card);
  set("--kp-shadow-soft", tokens.shadow.soft);
  set("--kp-shadow-lift", tokens.shadow.lift);
  set("--kp-line-strong", tokens.lineStrong);
  set("--kp-elevated", tokens.elevated);
  set("--kp-motion-fast", tokens.motion.fast);
  set("--kp-motion", tokens.motion.normal);
  set("--kp-font", tokens.font);
  set("--kp-success", tokens.semantic.success);
  set("--kp-warning", tokens.semantic.warning);
  set("--kp-error", tokens.semantic.error);
  set("--kp-info", tokens.semantic.info);
  set("--primaryColor", tokens.brand);
}

export default getTokens;
